<template>
    <div id="ListComment">
        <div v-for="comment in comments" :key="comment._id" class="comment-item">
            <div class="comment-header">
                <Avatar
                    v-if="usersMap[comment.user]"
                    :size="35"
                    :src="usersMap[comment.user].gravatar"
                    :username="usersMap[comment.user].name"
                />
                <span class="comment-username">
                    {{ usersMap[comment.user] ? usersMap[comment.user].name : "Unknown User" }}
                    <span v-if="comment.edited" class="edited-label">{{ $t("task.edited") }}</span>
                </span>
                <span class="comment-date">{{ formatDate(comment.createdAt) }}</span>
            </div>
            <div class="comment-body">
                <div v-if="editingCommentId === comment._id">
                    <textarea v-model="editedCommentMessage"></textarea>
                    <button @click="cancelEdit">{{ $t("calendar.cancel") }}</button>
                    <button @click="saveComment(comment._id)">{{ $t("calendar.save") }}</button>
                </div>
                <div v-else>
                    {{ comment.message }}
                </div>
                <div class="comment-actions" v-if="editingCommentId !== comment._id">
                    <a @click="editComment(comment)">{{ $t("chatbotv2.editBlock") }}</a>
                    -
                    <a @click="confirmDeleteComment(comment._id)">{{ $t("chatbotv2.delete") }}</a>
                </div>
            </div>
        </div>
        <div v-if="showDeleteConfirm" class="delete-confirm-overlay" @click="cancelDelete">
            <div class="delete-confirm-dialog" @click.stop>
                <div class="confirm-content">
                    <svg class="alert-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12" y2="16"></line>
                    </svg>
                    <p>{{ $t("task.deleteComment") }}</p>
                </div>
                <button @click="deleteComment(deleteCommentId)">{{ $t("message.yes") }}</button>
                <button @click="cancelDelete">{{ $t("message.no") }}</button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import Avatar from "vue-avatar";
import moment from "moment";

export default {
    name: "ListComment",
    props: {
        taskId: {
            type: String,
            required: true,
        },
    },
    components: {
        Avatar,
    },
    data() {
        return {
            editingCommentId: null,
            editedCommentMessage: "",
            showDeleteConfirm: false,
            deleteCommentId: null,
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
            usersMap: (state) =>
                state.user.users.data.reduce((acc, user) => {
                    acc[user._id] = user;
                    return acc;
                }, {}),
            comments: (state) => state.comments.comments,
        }),
    },
    watch: {
        comments: {
            handler(newComments) {
                console.log("Comments changed:", newComments);
            },
            deep: true,
        },
    },
    methods: {
        ...mapActions({
            fetchCommentsAction: "comments/fetchComments",
            deleteCommentAction: "comments/deleteComment",
            updateCommentAction: "comments/updateComment",
        }),
        async fetchComments() {
            try {
                await this.fetchCommentsAction({
                    siteId: this.site._id,
                    type: "task",
                    id: this.taskId,
                });
            } catch (error) {
                console.error("Error fetching comments:", error);
            }
        },
        async deleteComment(commentId) {
            try {
                await this.deleteCommentAction({
                    siteId: this.site._id,
                    id: commentId,
                });
                this.showDeleteConfirm = false;
                this.deleteCommentId = null;
            } catch (error) {
                console.error("Error deleting comment:", error);
            }
        },
        confirmDeleteComment(commentId) {
            this.showDeleteConfirm = true;
            this.deleteCommentId = commentId;
        },
        cancelDelete() {
            this.showDeleteConfirm = false;
            this.deleteCommentId = null;
        },
        editComment(comment) {
            this.editingCommentId = comment._id;
            this.editedCommentMessage = comment.message;
        },
        async saveComment(commentId) {
            try {
                await this.updateCommentAction({
                    siteId: this.site._id,
                    id: commentId,
                    newMessage: this.editedCommentMessage,
                });
                this.editingCommentId = null;
                this.editedCommentMessage = "";
                this.fetchComments();
            } catch (error) {
                console.error("Error updating comment:", error);
            }
        },
        cancelEdit() {
            this.editingCommentId = null;
            this.editedCommentMessage = "";
        },
        formatDate(date) {
            return moment(date).format("DD MMM YYYY, HH:mm");
        },
    },
    created() {
        this.fetchComments();
    },
};
</script>

<style lang="scss" scoped>
#ListComment {
    .edited-label {
        display: inline-block;
        background-color: #e0e0e0;
        color: #333;
        padding: 2px 8px;
        margin-left: 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: bold;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .comment-item {
        background-color: #ffffff;
        padding: 20px;
        border-radius: 8px;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        margin-bottom: 20px;
        transition: background-color 0.3s, box-shadow 0.3s;
        max-width: 600px;

        &:hover {
            background-color: #f0f0f0;
            box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
        }

        .comment-header {
            display: flex;
            align-items: center;
            gap: 15px;
            margin-bottom: 15px;

            .avatar {
                width: 45px;
                height: 45px;
                border-radius: 50%;
                object-fit: cover;
            }

            .comment-username {
                font-weight: bold;
                font-size: 16px;
                color: #333;
            }

            .comment-date {
                font-size: 14px;
                color: #777;
                margin-left: auto;
            }
        }

        .comment-body {
            font-size: 15px;
            color: #444;

            textarea {
                width: 100%;
                height: 120px;
                padding: 10px;
                border: 1px solid #ddd;
                border-radius: 4px;
                margin-bottom: 10px;
                font-size: 14px;
                color: #333;
                resize: vertical;
            }

            button {
                padding: 10px 15px;
                border: none;
                border-radius: 4px;
                font-size: 14px;
                cursor: pointer;
                transition: background-color 0.3s;

                &:first-of-type {
                    background-color: #f44336;
                    color: white;
                    margin-right: 10px;

                    &:hover {
                        background-color: #e53935;

                    }
                }

                &:last-of-type {
                    background-color: #4CAF50;
                    color: white;

                    &:hover {
                        background-color: #45a049;
                    }
                }
            }
        }

        .comment-actions {
            margin-top: 10px;

            a {
                color: #ec4b1d;
                cursor: pointer;
                text-decoration: none;
                margin-right: 10px;
                transition: color 0.3s;

                &:hover {
                    color: #d43f1d;
                }
            }
        }
    }

    .delete-confirm-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 9999;
    }

    .delete-confirm-dialog {
        background: #fff;
        padding: 20px;
        border-radius: 8px;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        text-align: center;

        .confirm-content {
            display: flex;
            align-items: center;
            gap: 15px;
            margin-bottom: 20px;

            .alert-icon {
                width: 24px;
                height: 24px;
                color: #f44336;
                margin-top: -14px;
            }

            p {
                font-size: 16px;
                color: #333;
            }
        }

        button {
            padding: 10px 15px;
            border: none;
            border-radius: 4px;
            font-size: 14px;
            cursor: pointer;
            transition: background-color 0.3s;
            margin: 0 5px;

            &:first-of-type {
                background-color: #4CAF50;
                color: white;

                &:hover {
                    background-color: #45a049;
                }
            }

            &:last-of-type {
                background-color: #f44336;
                color: white;

                &:hover {
                    background-color: #e53935;
                }
            }
        }
    }
}
</style>
